import React, { useRef, useEffect, useCallback, useState } from "react";
import { makeStyles } from "tss-react/mui";

import Webcam from "./webcam/resmed-react-webcam.tsx";

import { scanningMachine } from "./scanningMachine";
import { useMachine } from "@xstate/react";

import ImageGrabber from "./ImageGrabber.js";

import ovalOverlay from "./img/gradient_frame_transparent.png";

import crossHair from "./img/crosshair.png";
import doubleCrossHair from "./img/double_crosshair.png";

import * as tf from "@tensorflow/tfjs-core";

import * as ms from "resmed-mugshot-image-analyzer";

import RecordingDot from "./RecordingDot";
import { useTranslation } from "react-i18next";

import { getFaceAttributes, getFaceInfo } from "./ImageErrorCheck";

import MaskSelectorToolStyles from "../../../pages/MaskSelectorTool/MaskSelectorToolStyles"

import ChooseCameraDialog from "./ChooseCameraDialog";

import {
  SCAN_TIMEOUT_IN_MILLISECONDS,
  GLASSES_OFF_DIFFERENCE_THRESHOLD,
  GLASSES_ON_DIFFERENCE_THRESHOLD,
  GLASSES_CHECK_ENABLED,
  SCAN_INTERVAL_IN_MILLISECONDS,
  MINIMUM_SCAN_TIME_IN_MILLISECONDS,
  IDEAL_Y_FRONT_ANGLE,
  IDEAL_Y_LEFT_ANGLE,
  IDEAL_Y_RIGHT_ANGLE,
  Y_ANGLE_DISTANCE_VARIANCE,
  BLUR_DETECTION_ENABLED,
  MOUTH_OPEN_CHECK_ENABLED,
  SHOW_CAMERA_DENIED_DIALOG_INTERVAL_IN_MILLISECONDS,
  SHOW_DOUBLE_CROSSHAIR
} from "./MugShotConfig";

import {
  addCameraEntryForMediaStream,
  haveEntryForDeviceId,
  getMediaStreamAndDevices
}
  from "../../ChooseCameraUtils";

import { useInterval } from "../../CommonReact";

import { isMobile } from "../../Util"
import { Button, Typography, Box } from "@mui/material";

export const useStyles = makeStyles()((theme) => ({
  root: {
    textAlign: "center",
    position: "relative",
    width: "100%",
  },
  message: {
    position: "absolute",
    top: "5%",
    width: "100%",
    color: "rgba(255, 255, 255, 1.0)",
  },
  timer: {
    position: "absolute",
    top: "10%",
    width: "100%",
    color: "rgba(255, 255, 255, 1.0)",
    display: "block",
    fontSize: 45,
  },
  arrows: {
    minHeight: "72px",
  },

  imgOverlayBox: {
    width: "100%",
    position: "relative",
    overflow: "hidden"
  },

  crossHairImg: {
    top: "-5px",
    left: 0,
    right: 0,
    bottom: 0,
    position: "absolute",
    zIndex: 100,
    width: "77%",
    height: "auto",
    margin: "auto",
  },

  portrait: {
    top: 0,
    bottom: 0,
    zIndex: 100,
  },

  overlay: {
    position: "absolute",
  },

  messageBox: {
    maxWidth: 480,
    height: 53,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 48,
    marginBottom: 12,
    fontSize: "16px",

    "@media only screen and (max-width: 639px)": {
      marginTop: 32
    }
  },

  debugAngles: {
    marginTop: 36,
    fontSize: "20px",
  },

  scanningBox: {
    marginTop: 21,
    display: "flex",
    justifyContent: "center",
    marginBottom: 54,

    "@media only screen and (max-width: 639px)": {
      marginBottom: 11
    }
  },

  scanning: {
    fontSize: "24px",
  },

  scanIconBox: {
    marginTop: 38,
    marginBottom: 40,
  },

  webCamBox: {
    top: 0,
    position: "absolute",
  },

  webCam: {
    padding: "1px"
  },

  container: { position: "relative" },

  textButton: {
    border: "none", 
    background: "none", 
    cursor: "pointer"
  }

}));

const SCREENSHOT_QUALITY = 1;

const FaceScanner = ({
  model,
  landmarks,
  onComplete,
  onUserMediaError,
  scannerStateListener = undefined,
  imageCaptureListener = undefined,
  timeoutLength = SCAN_TIMEOUT_IN_MILLISECONDS,
  interval = SCAN_INTERVAL_IN_MILLISECONDS,
  cameraId = undefined,
  setCameraId = undefined,
  showNoCameraAccessDialog = false,
  cameraInfoArray,
  setCameraInfoArray,
}) => {
  const {classes} = useStyles();
  const {classes: maskSelectorClasses} = MaskSelectorToolStyles();

  const { t } = useTranslation("pages.MaskSelectorTool.FaceScanner");

  // Localization strings
  const instructions = t("instructions");
  const scanning = t("scanning");
  const reSelectCamera = t("reSelectCamera");

  const isNotMobile = isMobile() === 'false';
  const isPhone = isMobile() === 'true';

  const webcamRef = useRef(null);
  const headPoseRef = useRef();
  const fmesh = useRef(new ms.FaceIrisMesh());

  const VIDEO_WIDTH = 720;
  const VIDEO_HEIGHT = 720;

  const CAMERA_MEDIA_DEVICE_TYPE = "videoinput";

  const [runInterval, setRunInterval] = useState(false);

  const [scanInfo, setScanInfo] = useState([]);

  const [, setWidth] = useState(null);
  const [, setHeight] = useState(null);

  const [face, setFace] = useState(null);

  const [capturedImage, setCapturedImage] = useState(undefined);

  const [detectGlasses, setDetectGlasses] = useState(true);
  const [glassesOn, setGlassesOn] = useState(false);
  const [glassesOnDifferenceCount, setGlassesOnDifferenceCount] = useState(0);
  const [glassesOffDifferenceCount, setGlassesOffDifferenceCount] = useState(0);

  const [timeoutTimer, setTimeoutTimer] = useState(null);

  const [initialScanTime, setInitialScanTime] = useState(new Date().getTime());

  const scanningRef = useRef(null);

  const [cameras, setCameras] = useState([]);

  // choosingCameraInternal is used when testing the state of choosing a camera
  // inside our UseEffect call.
  const [choosingCameraInternal, setChoosingCameraInternal] = useState(false);
  // choosingCamera controls whether the camera choice dialog is displayed.  We
  // have a slight delay on display of this dialog to avoid flicker issues.
  const [choosingCamera, setChoosingCamera] = useState(false);

  const [enumeratingCameras, setEnumeratingCameras] = useState(false);

  // The function to enumerate the cameras only works over https or from localhost.
  // Calling this function otherwise will throw an exception, so we use this to keep
  // track of whether it's safe to call.
  const [cameraChoiceSupported, setCameraChoiceSupported] = useState(false);

  const [cameraReady, setCameraReady] = useState(false);

  //
  // state obj --> https://xstate.js.org/docs/guides/states.html
  // service obj -->
  //
  const [state, send, service] = useMachine(scanningMachine);

  const turnOffIntervals = () => {
    setRunInterval(false);
  };

  const haveWebCamData = () => {
    return takenWebCaptures();
  }

  const takenWebCaptures = () => {
    if ( cameraInfoArray === undefined || cameraInfoArray === null ) {
      return false;
    }

    if ( cameraInfoArray.length > 0 ) {
      return true;
    }

    return false;
  }

  const chooseCamera = () => {
    // Reset the scanning machine, so we don't start the timeout countdown until a new camera is chosen
    turnOffIntervals();
    resetMachineState();
    send("RESET");

    setCameraReady(false);

    // We need to display the camera choice dialog with a delay, otherwise the calls
    // to iterate through the camera properties and tracks cause a flicker on the screen
    // which makes the entire screen repaint in certain browsers.
    setTimeout(setChoosingCamera, 800 , true);
    setChoosingCameraInternal(true);

    // When choosing a camera the camera feed may load behind the dialog causing onUserMedia to load and
    // the interval to start which causes a flicker.
    setTimeout(stopMachine, 600 , false);
  };


  useEffect(() => {
    // We can't have an async useEffect callback function.
    // So instead, use an async IIFE.
    (async function() {

      const canEnumerateCameras = navigator?.mediaDevices?.enumerateDevices !== undefined;
      const canChooseCamera = isNotMobile && canEnumerateCameras;
      setCameraChoiceSupported(canChooseCamera);

      if (showNoCameraAccessDialog) {
        return;
      }

      if (haveWebCamData() && choosingCameraInternal) {
        return;
      }

      if (haveWebCamData()) {
        // If we have web camera data, then we already got permission from the camera.  Don't ask again.
        setCameraReady(true);
        return;
      }

      if (canChooseCamera && !enumeratingCameras) {
        if (!cameras || cameras.length < 1) {
          setEnumeratingCameras(true);

          try {
            let {
              mediaStream, // MediaStream object. https://developer.mozilla.org/en-US/docs/Web/API/MediaStream
              mediaDevices, // An array of MediaDeviceInfo objects https://developer.mozilla.org/en-US/docs/Web/API/MediaDevices/enumerateDevices
              cameraAccessFailed
            } = await getMediaStreamAndDevices(VIDEO_WIDTH, VIDEO_HEIGHT);

            if (!cameraAccessFailed) {
              // Request devices a second time. For some unknown reason we don't always get every device on the first try.
              mediaDevices = await navigator.mediaDevices.enumerateDevices().catch(() => {
                cameraAccessFailed = true;
              });
            }

            if (cameraAccessFailed) {
              cameraDeniedOnDesktop("Camera Access failed.");
              return;
            }

            // Asking for permission will have turned on a camera. Turn it off for now.
            let tracks = mediaStream.getTracks();  // An array of MediaStreamTrack objects

            let filteredCameras = mediaDevices.filter(({kind}) => kind === CAMERA_MEDIA_DEVICE_TYPE);
            setCameras(filteredCameras);

            let hasMultipleCameras = filteredCameras.length > 1;


            let cameraInfoArr = [];

            for (const track in tracks) {

              let mediaStreamTrack = tracks[track]; // https://developer.mozilla.org/en-US/docs/Web/API/MediaStreamTrack

              if (takenWebCaptures() === false && hasMultipleCameras) {
                await addCameraEntryForMediaStream(cameraInfoArr, mediaStreamTrack);
              } else {
                // A desktop user with one camera.  If we get here, we got permission to use the camera.
                // Save the camera information to the camera info array so that we avoid unneeded getUserMedia and
                // and enumerateDevices again which will slow down showing the camera after a retry.
                let deviceId = mediaStreamTrack.getSettings().deviceId;

                cameraInfoArr.push({
                  deviceId: deviceId,
                  label: mediaStreamTrack.label,
                  image: "not taken"
                })

                setCameraInfoArray(cameraInfoArr);
              }

              mediaStreamTrack.stop();
            }

            if (takenWebCaptures() === false && hasMultipleCameras) {

              for (let i = 0; i < filteredCameras.length; i++) {
                let mediaDevice = filteredCameras[i];

                let deviceId = mediaDevice.deviceId;

                if (haveEntryForDeviceId(cameraInfoArr, deviceId)) {
                  // don't take a screen capture twice.  We already took it above.
                  continue;
                }

                let devicePromise = navigator.mediaDevices.getUserMedia({
                  video: {
                    width: VIDEO_WIDTH,
                    height: VIDEO_HEIGHT,
                    deviceId: {exact: deviceId}
                  }
                });

                let mediaStreamRet = await Promise.all([devicePromise]);

                let mediaStreamTrack = mediaStreamRet[0].getTracks()[0];

                await addCameraEntryForMediaStream(cameraInfoArr, mediaStreamTrack);

                mediaStreamTrack.stop();
              }

              setCameraInfoArray(cameraInfoArr);
            }


            let willChooseCamera = false;

            if (!cameraId && filteredCameras.length > 0) {
              setCameraId(filteredCameras[0].deviceId);
              willChooseCamera = true;
            } else {
              setCameraReady(true);
            }

            if (hasMultipleCameras && !choosingCameraInternal) {
              // Multiple cameras have been detected so let the patient choose one.
              if (willChooseCamera) {
                chooseCamera();
              }
            }

            setEnumeratingCameras(false);

          } catch (error) {
            cameraDeniedOnDesktop(error);
          }

        } else {
          if ((!choosingCameraInternal) && (!enumeratingCameras)) {
            setCameraReady(true);
          }
        }
      } else {
        if (!enumeratingCameras) {
          setCameraReady(true);
        }
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setCameras, cameras, setCameraChoiceSupported, setCameraReady, enumeratingCameras,
      setEnumeratingCameras, showNoCameraAccessDialog ]);


  useEffect( () => {

    const listener = (e) => {
      turnOffIntervals();

      if (state.matches("completeAfterBuffer")) {
        onComplete({
          front: e.data.frontImage,
          left: e.data.leftImage,
          right: e.data.rightImage,

          scan_info: e.data.scan_info
        });
      }
    };

    service.onDone(listener);

    return () => {
      service.off(listener);
    };
  }, [state, service, onComplete ]);



  const timeoutPostProcessing = useCallback(async (state) => {

    state.context.angles = {}

    save_scan_info_to_context(state);

    if ( state.context.frontImageData !== undefined ) {
      let imageStatus = await getFaceInfo(tf, state.context.frontImageData, model);
      state.context.angles.front = imageStatus;

      if ( imageStatus.eyesClosed ) { state.context.errors.eyesClosed = true; }
      if ( imageStatus.mouthOpen ) { state.context.errors.mouthOpen = true; }
      if ( imageStatus.glasses ) { state.context.errors.glassesOn = true; }

    }

    if ( state.context.leftImageData !== undefined ) {
      let imageStatus = await getFaceInfo(tf, state.context.leftImageData, model);
      state.context.angles.left = imageStatus;

      if ( imageStatus.mouthOpen ) { state.context.errors.mouthOpen = true; }
      if ( imageStatus.glasses ) { state.context.errors.glassesOn = true; }
    }

    if ( state.context.rightImageData !== undefined ) {
      let imageStatus = await getFaceInfo(tf, state.context.rightImageData, model);
      state.context.angles.right = imageStatus;

      if ( imageStatus.mouthOpen ) { state.context.errors.mouthOpen = true; }
      if ( imageStatus.glasses ) { state.context.errors.glassesOn = true; }
    }

    scannerStateListener(state.value, state.context);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const detectErrorsAsync = useCallback(async (state) => {

    state.context.angles = {}

    save_scan_info_to_context(state);

    let frontStatus = await getFaceAttributes(tf, state.context.frontImageData, model, GLASSES_CHECK_ENABLED);
    state.context.angles.front = frontStatus;

    let leftStatus = await getFaceAttributes(tf, state.context.leftImageData, model, true, GLASSES_CHECK_ENABLED);
    state.context.angles.left = leftStatus;

    let rightStatus = await getFaceAttributes(tf, state.context.rightImageData, model, true, GLASSES_CHECK_ENABLED);
    state.context.angles.right = rightStatus;

    let errorInfo = {
      eyesClosed: frontStatus.eyesClosed,
      glassesOn: GLASSES_CHECK_ENABLED ? frontStatus.glasses || leftStatus.glasses || rightStatus.glasses : false,
      mouthOpen: frontStatus.mouthOpen || leftStatus.mouthOpen || rightStatus.mouthOpen,
    }

    const errorOnMouthOpen = MOUTH_OPEN_CHECK_ENABLED ? errorInfo.mouthOpen : false;

    if ( errorInfo.eyesClosed || errorInfo.glassesOn || errorOnMouthOpen ) {
      state.context.errors.eyesClosed = errorInfo.eyesClosed;
      state.context.errors.glassesOn = errorInfo.glassesOn;
      state.context.errors.mouthOpen = errorInfo.mouthOpen;
      send("ERROR");
    } else {
      state.context.scan_info.mouth_open = errorInfo.mouthOpen;
      send("NEXT")
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save_scan_info_to_context = ( state ) => {

    state.context.scan_info = {
      timeout_length: SCAN_TIMEOUT_IN_MILLISECONDS,
      ideal_y_front_angle: IDEAL_Y_FRONT_ANGLE,
      ideal_y_left_angle: IDEAL_Y_LEFT_ANGLE,
      ideal_y_right_angle: IDEAL_Y_RIGHT_ANGLE,
      y_angle_distance_variance: Y_ANGLE_DISTANCE_VARIANCE,
      mouth_open_check_enabled: MOUTH_OPEN_CHECK_ENABLED,
      blur_detection_enabled: BLUR_DETECTION_ENABLED,
      glass_check_enabled: GLASSES_CHECK_ENABLED,
      user_agent: navigator.userAgent,
      is_mobile: isMobile(),
      head_pos_details: scanInfo
    }
  }

  useEffect(() => {

    if (state.matches("timeout")) {
      turnOffIntervals();
      state.context.errors.glassesOn = glassesOn;
      state.context.errors.timeout = true;

      if (scannerStateListener !== undefined) {
        timeoutPostProcessing(state);
      }

    } else {
      // stateListener is called for a timeout state after some asynchronous processing.  If images exist we
      // need to determine the head angles, etc.

      if (scannerStateListener !== undefined) {
        scannerStateListener(state.value, state.context);
      }
    }

    if (state.matches("analyseImagesForErrors")) {
      clearTimeout(timeoutTimer);
      turnOffIntervals();
      detectErrorsAsync(state);
    }

    // Make sure we have waited the minimum scan time.
    // For more details see MugShotConfig.js -> MINIMUM_SCAN_TIME_IN_MILLISECONDS
    if (state.value === "completeAfterBuffer") {
      turnOffIntervals();

      let now = new Date().getTime();
      let scanTime = now - initialScanTime;

      if (scanTime > MINIMUM_SCAN_TIME_IN_MILLISECONDS) {
        send("NEXT");
      } else {
        let bufferWaitTime = MINIMUM_SCAN_TIME_IN_MILLISECONDS - scanTime;
        setTimeout(send, bufferWaitTime, "NEXT");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.value, send]);


  // Ideally, the timeout signal would be sent to the image capture machine instead of the scanning machine.
  // We could refactor the scanning machine so that it doesn't need a submachine but we've chosen
  // not touch it right now.
  const sendTimeout = () => {

    const capturingImagingMachine = service.children.get("capturingImagingMachine");

    if (capturingImagingMachine) {

      state.context.frontImage = capturingImagingMachine._state.context.frontImage;
      state.context.leftImage = capturingImagingMachine._state.context.leftImage;
      state.context.rightImage = capturingImagingMachine._state.context.rightImage;
    }

    send("TIMEOUT");
  }

  // The state of the machine is persistent when we go to a retry page and back.
  // Reset the scanning machine to default values.
  const resetMachineState = () => {
    // clear errors
    state.context.errors.mouthOpen = false;
    state.context.errors.glassesOn = false;
    state.context.errors.eyesClosed = false;
    state.context.errors.timeout = false;

    // clear images
    state.context.frontImage = undefined;
    state.context.leftImage = undefined;
    state.context.rightImage = undefined;

    state.context.frontImageData = undefined;
    state.context.leftImageData = undefined;
    state.context.rightImageData = undefined;
  }

  const onUserMedia = (videoConstraints) => {

    if (isPhone) {
      // Stop the query camera loop on mobile devices.
      setCameraReady(true);
    }

    const video = webcamRef.current.video;

    video.onloadeddata = () => {
      setWidth(webcamRef.current.video.videoWidth);
      setHeight(webcamRef.current.video.videoHeight);

      if( cameraReady ) {
        startMachine();
      }

    };
  };

  const startMachine = () => {
    setRunInterval(true);

    resetMachineState();

    send("INITIALISED");

    let timer = setTimeout(sendTimeout, timeoutLength);
    setTimeoutTimer(timer);

    setInitialScanTime(new Date().getTime());
  }

  const stopMachine = () => {
    setRunInterval(false);
    clearTimeout(timeoutTimer);
  }

  const cameraDeniedOnDesktop = (err) => {

    // Due to the way the React useEffect call works, we will get here multiple times.
    // Simply clear the last timeout and set another so that the final call causes one
    // dialog to appear.
    clearTimeout(onUserMediaErrorInternal);
    setTimeout(onUserMediaErrorInternal, SHOW_CAMERA_DENIED_DIALOG_INTERVAL_IN_MILLISECONDS, err)
  }

  const onPhoneCallback = () => {
    setCameraReady(true);
  };

  const onDesktopCallback = () => {
    setEnumeratingCameras(false);
  };

  const onUserMediaErrorInternal = (err) => {
    if ( onUserMediaError !== undefined ) {
      onUserMediaError(err, isPhone ? onPhoneCallback : onDesktopCallback );

      // This will prompt the user again with the modal if they close the modal without
      // enabling camera permissions.
      setCameraReady(false);
    }
  };

  const updateFace = useCallback(async () => {
    try {
      let canvas = webcamRef.current.getScreenShotReturnCanvas();

      if (!canvas) {
        return [null, null, null];
      }

      const faces = await model.estimateFaces({
        input: canvas,
        predictIrises: false,
        detectGlasses: GLASSES_CHECK_ENABLED ? detectGlasses : false
      });

      if (faces.length < 1) {

        let now = new Date().getTime();
        let scanTime = now - initialScanTime;

        scanInfo.push( { face: false, glasses: false, scanTime: scanTime } );

        return [null, null, null];
      }

      let image = canvas.toDataURL("image/webp", SCREENSHOT_QUALITY)
      let imageData = canvas.getContext('2d').getImageData(0,0,720,720);

      return [faces[0], image, imageData];
    } catch (err) {
      return [null, null, null];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  useInterval(
    async () => {
      const [face, image, imageData] = await updateFace();
      setFace(face);

      if (face && image) {
        fmesh.current.updatePoints(face.scaledMesh);
        let lcsm = ms.getLandmarkCoordinates(fmesh.current, landmarks, false);

        // Get head orientation
        headPoseRef.current = ms.getHeadPose(lcsm);

        const [, eulerAngles] = ms.getHeadPose(lcsm);
        const eulerAnglesDeg = ms.getEulerAnglesDeg(eulerAngles);

        let blurInfo = {};

        if ( BLUR_DETECTION_ENABLED ) { blurInfo = await ms.getBlurInfoWithLandMarks(imageData, lcsm); }

        setCapturedImage({
          image: image,
          imageData: imageData,
          xAngle: eulerAnglesDeg.x,
          yAngle: eulerAnglesDeg.y,
          blur_edge_ratio: blurInfo.edge_ratio
        });

        let now = new Date().getTime();
        let scanTime = now - initialScanTime;

        let scanFrameInfo = build_scan_info(scanTime, eulerAnglesDeg, face.glasses, blurInfo);

        if (imageCaptureListener !== undefined ) {
          imageCaptureListener(image, blurInfo);
        }

        scanInfo.push(scanFrameInfo);

        const glassesDetected = face.glasses;
        if ( detectGlasses === false ) {
          return;
        }

        if (glassesOn) {
          if (!glassesDetected) {
            if (glassesOffDifferenceCount < GLASSES_OFF_DIFFERENCE_THRESHOLD) {
              setGlassesOffDifferenceCount(glassesOffDifferenceCount + 1);
            } else {
              setDetectGlasses(false);

              setGlassesOn(false);
              send("NO_GLASSES");
              clearTimeout(timeoutTimer);
              let timer = setTimeout(sendTimeout, timeoutLength);
              setTimeoutTimer(timer);

            }
          } else {
            setGlassesOffDifferenceCount(0);
          }

          return;
        }

        if (glassesDetected) {
          if (glassesOnDifferenceCount < GLASSES_ON_DIFFERENCE_THRESHOLD) {
            setGlassesOnDifferenceCount(glassesOnDifferenceCount + 1);
          } else {
            setGlassesOn(true);
            send("GLASSES_FOUND");
          }
        } else {
          setGlassesOnDifferenceCount(0);
        }

      } else {
        headPoseRef.current = null;
      }
    },
    runInterval ? interval : null
  );

  const build_scan_info = (scanTime, eulerAnglesDeg, glasses, blurInfo ) => {

    let blur_info_converted = { }

    if (blurInfo.width !== undefined) {
      blur_info_converted =
          {
            width: blurInfo.width,
            height: blurInfo.height,
            num_edges: blurInfo.num_edges,
            edge_ratio: blurInfo.edge_ratio.toFixed(4),
            avg_edge_width: blurInfo.avg_edge_width.toFixed(2),
            avg_edge_width_perc: blurInfo.avg_edge_width_perc.toFixed(2)
          }
    }

    return {
      face: true,
      glasses: glasses,
      scanTime: scanTime,
      x: eulerAnglesDeg.x.toFixed(2),
      y: eulerAnglesDeg.y.toFixed(2),
      blurInfo: {
        ...blur_info_converted
      }
    }
  }

  useEffect(() => {
    function handleResize() {
      setWidth(webcamRef.current.video.videoWidth);
      setHeight(webcamRef.current.video.videoHeight);
    }

    window.addEventListener("resize", handleResize);

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []); // Empty array ensures that effect is only run on mount

  const [firstRender, setFirstRender] = useState(true);

  useEffect(() => {
    // useEffect() will get called for every frame of the webcam video
    // but we only want to set the focus on the first render.
    if(scanningRef?.current && firstRender) {
      scanningRef.current.focus();
      setFirstRender(false);
    }
  });

  const FacingModeUser = "user";

  const videoConstraints = {
    width: VIDEO_WIDTH,
    height: VIDEO_HEIGHT,
    deviceId: (cameraChoiceSupported ? cameraId : undefined),
    facingMode: (!cameraChoiceSupported ? FacingModeUser : undefined)
  };



  const key = "capturingImagingMachine";
  const machine = service.children.get(key);

  const chooseCameraCallback = (cameraIdChosen) => {
    setChoosingCamera(false);
    setCameraReady(true);
    setCameraId(cameraIdChosen);
    startMachine();
  };

  return (
    <Box sx={{textAlign: "center"}}>
      {/*  Do not show camera choice dialog until camera access dialog has been quit. */}
      <ChooseCameraDialog isOpen={choosingCamera && !showNoCameraAccessDialog}
                          cameraId={cameraId}
                          cameraInfoArray={cameraInfoArray}
                          doneCallback={chooseCameraCallback}
      />

      <Box className={classes.scanningBox}>
        <RecordingDot />
        <Typography className={`${classes.scanning} ${maskSelectorClasses.noOutline}`} ref={scanningRef} tabIndex="0" aria-label={scanning}>{scanning}</Typography>
      </Box>

      <Box className={classes.container}>

        <Box className={classes.portrait}>

          <Box className={classes.imgOverlayBox}>
            <img src={ovalOverlay} alt="" width="100%" />
            <img src={SHOW_DOUBLE_CROSSHAIR ? doubleCrossHair : crossHair} alt="" className={classes.crossHairImg}/>
          </Box>

          <Box className={classes.messageBox}>
            <Typography tabIndex="0" aria-label={instructions}>{instructions}</Typography>
          </Box>

        </Box>

        <Box className={classes.webCamBox}>
          { cameraReady && (
            <Webcam
              audio={false}
              ref={webcamRef}
              videoConstraints={videoConstraints}
              mirrored={true}
              screenshotQuality={SCREENSHOT_QUALITY}
              forceScreenshotSourceSize={true}
              onUserMedia={() => onUserMedia(videoConstraints)}
              onUserMediaError={onUserMediaErrorInternal}
              className={classes.webCam}
            />
          )}
        </Box>

        <Box className={`${classes.portrait} ${classes.overlay}`}>
          <img src={ovalOverlay} alt="" width="100%" />
        </Box>

        { machine && (
          <ImageGrabber
          key={key}
          machineName={key}
          machine={machine}
          coords={face ? face.scaledMesh[landmarks.nose_tip] : null}
          currentCapturedImage={capturedImage}
          />
          )}
      </Box>

      { cameras.length > 1 && cameraChoiceSupported && (
        <Button disableRipple variant="text" className={`${maskSelectorClasses.res_standaloneLink} ${classes.textButton}`} onClick={ chooseCamera }>{ reSelectCamera }</Button>
      )}
    </Box>
  );
};

export default FaceScanner;
